import React, { FC, PropsWithChildren, useRef, useState } from "react";
import {
  ActionBarPrimitive,
  BranchPickerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  useAssistantRuntime,
  useMessage
} from "@assistant-ui/react";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, CopyIcon, ThumbsDown, ThumbsUp } from "lucide-react";
import { cn, formatCreatedAt } from "@/lib/utils";
import { TooltipIconButton } from "./tooltip-icon-button";
import { SourceMessagePart } from "./source-message-part";
import { ReasoningMessagePart } from "./reasoning-message-part";
import { MarkdownText } from "./markdown-text";
import { TypingIndicator } from "./ThreeDotsLoader";
import { FeedbackDialog } from "./feedback-dialog";
import { FeedbackTypeEnum } from "../../types";
import { dislikeMessage, likeMessage } from "api/axios";
import { useConfirmation } from "components/dialog/confirmation";
import { useSidebarCountsContext } from "contexts/SidebarCountsContext";
import { ArtifactPanel } from "@/components/artifact-panel";
import { Artifact } from "../../types/chat-types";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface ParentIdGroupProps {
  groupKey: string | undefined;
  indices: number[];
  title: string;
  existArtifact: boolean;
  setOpenArtifact: (value: { open: boolean; parentId: string }) => void;
}

export const ParentIdGroup: FC<PropsWithChildren<ParentIdGroupProps>> = ({
  groupKey,
  indices,
  children,
  title,
}) => {
  const { t } = useTranslation();
  const groupRef = useRef<HTMLDivElement>(null);

  if (!groupKey) {
    // Ungrouped parts - render directly with ordering class
    return <div className="ungrouped-content order-1">{children}</div>;
  }

  // Only apply ordering for reference groups
  const orderingClass =
    groupKey === "reference" ? "grouped-content order-2" : "";

  return (
    <div
      ref={groupRef}
      className={`bg-muted/20 my-3 rounded-lg border border-border ${orderingClass}`}
      data-parent-id={groupKey}
    >
      {/* Group Header */}
      <div className="hover:bg-muted/30 group flex w-full items-center justify-between rounded-t-lg p-3 transition-colors duration-200">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {title || t("chat.group")}
          </span>
          <span className="text-xs text-muted-foreground">
            ({indices.length} {t("chat.parts")})
          </span>
        </div>
      </div>

      {/* Group Content - Always Visible */}
      <div className="group-content space-y-2 px-3 pb-3 text-sm">
        {children}
      </div>
    </div>
  );
};

// Enhanced AssistantMessage component that supports ParentIdGrouping with CSS-based reordering
export const ParentIdGroupedMessage: FC<{
  threadId?: string;
  setOpenArtifact: (value: { open: boolean; parentId: string }) => void;
  openArtifact: { open: boolean; parentId: string };
}> = ({ threadId, setOpenArtifact, openArtifact }) => {
  const { t } = useTranslation();
  const message = useMessage();
  const runtime = useAssistantRuntime();
  const isHistoryMessage = message.id.length > 7;
  const { confirm } = useConfirmation();
  const { refetchFeedbackCount } = useSidebarCountsContext();
  const location = useLocation();
  const currentPath = location.pathname;
  const isLogsAgentPage = currentPath?.endsWith("/logs");

  // Initialize feedback state from message data
  const [feedback, setFeedback] = useState<FeedbackTypeEnum | null>(() => {
    // Check if message has feedback data (works for both new and old messages)
    const messageFeedback = (message as any).feedback;
    const isLike =
      (message as any).is_like ||
      message.submittedFeedback?.type === "positive";
    const isDislike =
      (message as any).is_dislike ||
      message.submittedFeedback?.type === "negative";

    // Handle different possible feedback formats
    if (messageFeedback !== undefined && messageFeedback !== null) {
      // If it's already in the correct format (0, 1, or null)
      if (typeof messageFeedback === "string") {
        return messageFeedback === "liked"
          ? FeedbackTypeEnum.CONTRIBUTE
          : messageFeedback === "disliked"
          ? FeedbackTypeEnum.DISLIKE
          : null; // or undefined, depending on how you handle no feedback
      }
    }

    // Handle new API format with is_like and is_dislike booleans
    if (isLike === true) {
      return FeedbackTypeEnum.CONTRIBUTE;
    }
    if (isDislike === true) {
      return FeedbackTypeEnum.DISLIKE;
    }

    // Default to no feedback
    return null;
  });

  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackTypeEnum>(
    FeedbackTypeEnum.CONTRIBUTE
  );

  const latestCreateAt = (message.metadata as any)?.custom?.latest_create_at as
    | string
    | undefined;
  const assistantMessageId = (message.metadata as any)?.custom
    ?.assistant_message_id as string | undefined;
  const displayCreatedAt =
    !isHistoryMessage && latestCreateAt
      ? new Date(latestCreateAt)
      : message.createdAt;

  // Determine which message ID to use for API calls based on message type
  // For new messages (!isHistoryMessage): use assistantMessageId if available, otherwise message.id
  // For history messages (isHistoryMessage): always use message.id
  const messageIdForApi =
    !isHistoryMessage && assistantMessageId ? assistantMessageId : message.id;

  const handleFeedback = async (type: FeedbackTypeEnum) => {
    // If feedback already exists, don't allow changes
    if (feedback !== null) {
      console.log(
        `Feedback already exists for message ${messageIdForApi}: ${feedback}`
      );
      return;
    }

    if (type === FeedbackTypeEnum.CONTRIBUTE) {
      // For likes, show confirmation dialog
      const isConfirmed = await confirm({
        title: t("chat.likeResponse"),
        message: t("chat.likeResponseConfirm"),
        confirmText: t("chat.like"),
        cancelText: t("common.cancel"),
        variant: "default",
      });

      if (isConfirmed) {
        // Call API first, then update state only if successful
        console.log("threadId", threadId);
        if (threadId) {
          try {
            runtime.thread
              .getMesssageById(message.id)
              .submitFeedback({ type: "positive" });
            await likeMessage(threadId, messageIdForApi);
            // Only update state after successful API call
            setFeedback(FeedbackTypeEnum.CONTRIBUTE);
            // Refresh sidebar feedback count
            await refetchFeedbackCount();
          } catch (error) {
            console.error("Failed to save like:", error);
            // toast.error('Failed to save like. Please try again.');
          }
        } else {
          console.warn("Thread ID not available for like action");
        }
      }
    } else {
      // For dislikes, show the feedback dialog as before
      setFeedbackType(type);
      // Add a small delay to prevent flashing
      requestAnimationFrame(() => {
        setShowFeedbackDialog(true);
      });
    }
  };

  const handleSubmitFeedback = async (feedbackText: string) => {
    try {
      const feedbackValue =
        feedbackType === FeedbackTypeEnum.CONTRIBUTE
          ? FeedbackTypeEnum.CONTRIBUTE
          : FeedbackTypeEnum.DISLIKE;
      console.log(
        `Attempting to ${feedbackType} message ${messageIdForApi}: ${feedbackText}`
      );

      // Call appropriate API based on feedback type
      if (threadId) {
        if (feedbackType === FeedbackTypeEnum.CONTRIBUTE) {
          runtime.thread
            .getMesssageById(message.id)
            .submitFeedback({ type: "positive" });
          await likeMessage(threadId, messageIdForApi);
        } else {
          runtime.thread
            .getMesssageById(message.id)
            .submitFeedback({ type: "negative" });
          await dislikeMessage(threadId, messageIdForApi, feedbackText);
        }
        console.log(`Feedback saved for message ${messageIdForApi}`);
        // Only update state after successful API call
        setFeedback(feedbackValue);
        // Refresh sidebar feedback count
        await refetchFeedbackCount();
      } else {
        console.warn("Thread ID not available for feedback action");
      }
    } catch (error) {
      console.error("Failed to save feedback:", error);
      // You might want to show a toast notification here
      // toast.error('Failed to save feedback. Please try again.');
    }
  };

  const artifactData = message?.metadata?.custom?.artifact as Artifact;
  if (
    !openArtifact?.open &&
    message?.status?.type === "running" &&
    artifactData?.text
  ) {
    setOpenArtifact({ open: true, parentId: message?.parentId });
  }

  const handleCloseArtifact = () => {
    setOpenArtifact({ open: false, parentId: "" });
  };

  const handleViewInArtifact = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setOpenArtifact({ open: true, parentId: message?.parentId });
  };

  return (
    <>
      {message?.parentId === openArtifact?.parentId && (
        <ArtifactPanel
          isOpen={openArtifact?.open}
          onClose={handleCloseArtifact}
          title={t("chat.researchArtifact")}
          artifact={artifactData}
          role={message?.role}
        />
      )}
      <MessagePrimitive.Root className="mx-auto w-full max-w-4xl py-6">
        <div className="flex justify-start px-4">
          <div className="flex w-full max-w-4xl flex-col">
            <div
              className="custom-message-order flex flex-col rounded-2xl bg-muted px-4 py-2.5 text-sm leading-relaxed"
              data-assistant-message-content
            >
              <MessagePrimitive.Unstable_PartsGroupedByParentId
                components={{
                  Text: MarkdownText,
                  Group: (props) => {
                    return (
                      <ParentIdGroup
                        {...props}
                        existArtifact={!!artifactData?.text}
                        setOpenArtifact={setOpenArtifact}
                        title={
                          props.groupKey === "reasoning"
                            ? t("chat.thinking")
                            : t("chat.reference")
                        }
                      />
                    );
                  },
                  Source: (props) => <SourceMessagePart {...props} />,
                  Reasoning: (props) => <ReasoningMessagePart {...props} />,
                  Empty: (_props) => <TypingIndicator />,
                }}
              />

              {!!artifactData?.text && (
                <button
                  onClick={handleViewInArtifact}
                  className="group mt-3 flex w-full items-center justify-between rounded-md border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition-all duration-200 hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <div className="flex flex-col items-start">
                    <span>{t("chat.viewInArtifact")}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {t("chat.clickToOpenInArtifact")}
                    </span>
                  </div>
                  <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              )}
              <MessageError />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <div
                className={cn("mt-2 flex items-center gap-1", {
                  hidden: isLogsAgentPage,
                })}
              >
                <AssistantActionBar />
                {/* Like/Dislike buttons - only show when message is complete */}
                {message.status.type === "complete" && (
                  <div className="flex items-center gap-1">
                    <ActionBarPrimitive.FeedbackPositive asChild>
                      {feedback !== FeedbackTypeEnum.DISLIKE && (
                        <TooltipIconButton
                          tooltip={
                            feedback === FeedbackTypeEnum.CONTRIBUTE
                              ? t("chat.youLikedResponse")
                              : t("chat.likeThisResponse")
                          }
                          variant="ghost"
                          className={cn(
                            "h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground",
                            feedback === FeedbackTypeEnum.CONTRIBUTE &&
                              "bg-gray-100 dark:bg-gray-800",
                            feedback === FeedbackTypeEnum.CONTRIBUTE &&
                              "cursor-not-allowed opacity-50"
                          )}
                          onClick={() =>
                            handleFeedback(FeedbackTypeEnum.CONTRIBUTE)
                          }
                          disabled={feedback === FeedbackTypeEnum.CONTRIBUTE}
                        >
                          <ThumbsUp
                            className="h-4 w-4"
                            fill={
                              feedback === FeedbackTypeEnum.CONTRIBUTE
                                ? "currentColor"
                                : "none"
                            }
                          />
                        </TooltipIconButton>
                      )}
                    </ActionBarPrimitive.FeedbackPositive>
                    <ActionBarPrimitive.FeedbackNegative asChild>
                      {feedback !== FeedbackTypeEnum.CONTRIBUTE && (
                        <TooltipIconButton
                          tooltip={
                            feedback === FeedbackTypeEnum.DISLIKE
                              ? t("chat.youDislikedResponse")
                              : t("chat.dislikeThisResponse")
                          }
                          variant="ghost"
                          className={cn(
                            "h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground",
                            feedback === FeedbackTypeEnum.DISLIKE &&
                              "bg-gray-100 dark:bg-gray-800",
                            feedback === FeedbackTypeEnum.DISLIKE &&
                              "cursor-not-allowed opacity-50"
                          )}
                          onClick={() =>
                            handleFeedback(FeedbackTypeEnum.DISLIKE)
                          }
                          disabled={feedback === FeedbackTypeEnum.DISLIKE}
                        >
                          <ThumbsDown
                            className="h-4 w-4"
                            fill={
                              feedback === FeedbackTypeEnum.DISLIKE
                                ? "currentColor"
                                : "none"
                            }
                          />
                        </TooltipIconButton>
                      )}
                    </ActionBarPrimitive.FeedbackNegative>
                  </div>
                )}
              </div>
              {message.status.type === "complete" && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatCreatedAt(displayCreatedAt)}
                </p>
              )}
            </div>
          </div>
        </div>
        <BranchPicker className="mt-2 flex justify-start px-4" />
      </MessagePrimitive.Root>

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        onSubmit={handleSubmitFeedback}
        feedbackType={feedbackType}
        messageId={message.id}
      />
    </>
  );
};

// Supporting components
const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="bg-destructive/10 dark:bg-destructive/5 mt-2 rounded-md border border-destructive p-3 text-sm text-destructive dark:text-red-200">
        <ErrorPrimitive.Message className="line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

const AssistantActionBar: FC = () => {
  const { t } = useTranslation();
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="never"
      autohideFloat="single-branch"
      className="flex items-center gap-1"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton
          tooltip={t("chat.copyMessage")}
          variant="ghost"
          className="h-8 w-8 rounded-lg p-1.5 text-muted-foreground transition-colors duration-200 hover:bg-accent hover:text-foreground"
        >
          <MessagePrimitive.If copied>
            <CheckIcon className="h-4 w-4" />
          </MessagePrimitive.If>
          <MessagePrimitive.If copied={false}>
            <CopyIcon className="h-4 w-4" />
          </MessagePrimitive.If>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
    </ActionBarPrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "inline-flex items-center text-xs text-muted-foreground",
        className
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip={t("common.previous")}>
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip={t("common.next")}>
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
